// views/Projects.vue
<template>
  <v-container fluid>
    <!-- Top filter/search area -->
    <v-row>
      <v-col cols="12">
        <v-card class="mb-6">
          <v-card-title class="py-2">
            <span class="text-h5">Project</span>
            <v-spacer></v-spacer>
            <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search projects..."
                single-line
                hide-details
                outlined
                dense
                class="mx-4"
                style="max-width: 300px"
            ></v-text-field>
            <v-btn color="primary" @click="refreshProjects" :loading="loading">
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Project list by group -->
    <v-row>
      <v-col cols="12">
        <v-expansion-panels>
          <v-expansion-panel
              v-for="(group, groupPath) in groupedProjects"
              :key="groupPath"
              :loading="loading"
          >
            <v-expansion-panel-header class="title">
              <v-row no-gutters align="center">
                <v-icon class="mr-4">{{ group.expanded ? 'mdi-folder-open' : 'mdi-folder' }}</v-icon>
                <span class="text-h6">{{ groupPath }}</span>
                <span class="ml-4 grey--text">({{ group.projects.length }} projects)</span>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-6">
              <v-row>
                <v-col
                    v-for="project in filterProjects(group.projects)"
                    :key="project.id"
                    cols="12"
                    sm="6"
                    lg="4"
                    xl="3"
                >
                  <project-card
                      :project="project"
                      @show-details="showProjectDetails"
                  ></project-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Project details dialog -->
    <project-details-dialog
        v-model="detailsDialog"
        :project="selectedProject"
        :project-details="projectDetails"
        :loading="detailsLoading"
    ></project-details-dialog>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import gitlab from "@/config/api/gitlab";
import ProjectCard from "@/components/gitlab/ProjectCard.vue";
import ProjectDetailsDialog from "@/components/gitlab/ProjectDetailsDialog.vue";

export default {
  name: 'Projects',

  components: {
    ProjectCard,
    ProjectDetailsDialog
  },

  data: () => ({
    projects: [],
    search: '',
    loading: false,
    detailsLoading: false,
    detailsDialog: false,
    selectedProject: null,
    projectDetails: null,
    groupExpanded: {}
  }),

  computed: {
    groupedProjects() {
      const groups = {};
      this.projects.forEach(project => {
        const groupPath = project.namespace?.fullPath || 'Others';
        if (!groups[groupPath]) {
          groups[groupPath] = {
            expanded: this.groupExpanded[groupPath] ?? true,
            projects: []
          };
        }
        groups[groupPath].projects.push(project);
      });

      // Sort projects within groups
      Object.values(groups).forEach(group => {
        group.projects.sort((a, b) => a.name.localeCompare(b.name));
      });

      return groups;
    }
  },

  methods: {
    async refreshProjects() {
      this.loading = true;
      try {
        const response = await gitlab.pjList({
          token: this.$store.getters.getUser.gitlabAccessToken,
          membership: true
        });
        this.projects = response;
      } catch (error) {
        console.error('Failed to load project list:', error);
        this.$notify.error('Error', 'Failed to load project list');
      } finally {
        this.loading = false;
      }
    },

    async showProjectDetails(project) {
      this.selectedProject = project;
      this.detailsDialog = true;
      this.detailsLoading = true;
      this.projectDetails = null;

      try {
        const details = await gitlab.projectDetails({
          projectId: project.id,
          token: this.$store.getters.getUser.gitlabAccessToken
        });
        this.projectDetails = details;
      } catch (error) {
        console.error(`Failed to load project details (${project.name}):`, error);
        this.$notify.error('Error', `Failed to load project details for ${project.name}`);
      } finally {
        this.detailsLoading = false;
      }
    },

    filterProjects(projects) {
      if (!this.search) return projects;

      const searchLower = this.search.toLowerCase();
      return projects.filter(project =>
          project.name.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.namespace?.name?.toLowerCase().includes(searchLower) ||
          project.topics?.some(topic => topic.toLowerCase().includes(searchLower))
      );
    }
  },

  mounted() {
    this.refreshProjects();
  }
};
</script>

<style scoped>
.v-card.project-card {
  height: 100%;
}

.v-expansion-panel-header {
  background-color: #f5f5f5;
}

.v-expansion-panel-content {
  background-color: #ffffff;
}
</style>